import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  PenTool, Code2, Gauge, Search, Smartphone, ShieldCheck,
  ArrowRight, Check, Activity, Lock, RefreshCcw, Zap, Phone, Mail,
} from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "../components/anim";
import Marquee from "../components/Marquee";
import Starfield from "../components/Starfield";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SERVICES, PROCESS_STEPS, FAQS, IMAGES } from "../data/site";

const ICONS = { PenTool, Code2, Gauge, Search, Smartphone, ShieldCheck };

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 90]);
  const rotate = useTransform(scrollY, [0, 700], [-2, 3]);

  return (
    <section className="relative overflow-hidden bg-[#050816] pt-36 pb-20 lg:pt-44 lg:pb-28">
      <div className="bg-grid-dark absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-blue-600/25 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-[-25%] left-[-10%] h-[480px] w-[480px] rounded-full bg-violet-600/20 blur-[130px]" />
      <div className="pointer-events-none absolute top-[35%] left-[30%] h-[320px] w-[320px] rounded-full bg-cyan-500/10 blur-[110px]" />
      <Starfield />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>Web Design · Build · Launch</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.1}>We design it.</MaskedLine>
            <MaskedLine delay={0.24}>We launch it.</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-blue-400">We care for it.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300"
          >
            G-Studio designs and builds high-performance business websites — expertly developed, mobile-first
            and launched in days. Book a free call and we'll take it from there.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="tel:+15078411801"
              data-testid="hero-call-btn"
              className="group flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-700 hover:shadow-blue-600/40"
            >
              <Phone className="h-4 w-4" />
              Call (507) 841-1801
            </a>
            <a
              href="#book"
              data-testid="hero-book-btn"
              className="rounded-full border border-white/20 bg-white/5/5 px-7 py-4 text-sm font-semibold text-white backdrop-blur transition-colors hover:border-blue-400 hover:text-blue-300"
            >
              Book a Free Call
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-2 font-mono2 text-xs font-medium uppercase tracking-[0.16em] text-slate-400"
            data-testid="hero-trust-row"
          >
            <span>Under a Week to Launch</span>
            <span>Free Intro Call</span>
            <span>Fully Managed</span>
          </motion.div>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -4 }}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            style={{ y, rotate }}
            className="relative rounded-2xl border border-white/10 bg-white/5 shadow-2xl shadow-slate-900/10"
            data-testid="hero-preview-card"
          >
            <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="ml-3 flex-1 rounded-md bg-slate-100 px-3 py-1 font-mono2 text-[10px] text-slate-400">
                yourbusiness.com
              </span>
            </div>
            <img src={IMAGES.workspace} alt="G-Studio website preview" className="h-64 w-full rounded-b-2xl object-cover sm:h-72" />
            <div className="absolute -bottom-5 -left-4 flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 shadow-xl sm:-left-8">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50">
                <Activity className="h-4 w-4 text-emerald-400" />
              </span>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Uptime · 30d</p>
                <p className="font-display text-sm font-bold text-white">99.99%</p>
              </div>
            </div>
            <div className="absolute -top-5 -right-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 shadow-xl sm:-right-6">
              <Lock className="h-4 w-4 text-blue-400" />
              <p className="text-xs font-semibold text-slate-200">Secured & backed up</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="relative overflow-hidden py-20 lg:py-28">
      <Starfield density={0.00005} />
      <div className="pointer-events-none absolute top-[-20%] left-[40%] h-[400px] w-[400px] rounded-full bg-violet-600/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>What We Do</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Everything your website needs. Nothing it doesn't.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.title} delay={i * 0.07}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group h-full rounded-2xl border border-white/10 bg-white/5 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-blue-600/40 hover:shadow-xl hover:shadow-blue-600/5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 transition-colors group-hover:bg-blue-600">
                    <Icon className="h-5 w-5 text-blue-400 transition-colors group-hover:text-white" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section id="process" className="border-y border-white/10 bg-[#070B1E] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>The Process</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Four chapters. One launched website.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-2">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.08}>
              <div className="flex gap-6 border-t-2 border-blue-500/40 pt-6" data-testid={`process-step-${step.num}`}>
                <span className="font-display text-5xl font-bold tracking-tight text-blue-400/25">{step.num}</span>
                <div>
                  <h3 className="font-display text-xl font-semibold tracking-tight text-white">{step.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Upkeep() {
  const bars = [38, 52, 44, 66, 58, 74, 68, 82, 76, 90, 84, 96];
  return (
    <section className="relative overflow-hidden bg-[#070B1E] py-20 lg:py-28">
      <div className="bg-grid-dark absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute top-[-30%] left-[30%] h-[500px] w-[500px] rounded-full bg-blue-600/25 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-[-20%] right-[-5%] h-[400px] w-[400px] rounded-full bg-violet-600/15 blur-[120px]" />
      <Starfield />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <Eyebrow dark>After Launch</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            A website isn't a painting. It's a machine.
          </h2>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-slate-400">
            Machines need maintenance. Security threats, slow pages and stale content quietly cost you customers.
            That's why we stay one email away after launch — whenever you need a change, we get back to you fast and handle
            it quickly.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: Lock, text: "Security patches & vulnerability scans before hackers find the gaps" },
              { icon: Zap, text: "Speed monitoring — slow pages lose half their visitors" },
              { icon: RefreshCcw, text: "Fresh content and updates that keep Google (and customers) interested" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm text-slate-300">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-600/20">
                  <Icon className="h-3.5 w-3.5 text-blue-400" />
                </span>
                {text}
              </li>
            ))}
          </ul>
          <a
            href="#book"
            data-testid="upkeep-book-btn"
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600 hover:text-white"
          >
            Book a Free Call
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-white/5/5 p-7 backdrop-blur" data-testid="health-widget">
            <div className="flex items-center justify-between">
              <p className="font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Site Health — Live
              </p>
              <span className="flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 font-mono2 text-[10px] font-semibold uppercase tracking-widest text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" /> All Systems Go
              </span>
            </div>
            <div className="mt-6 flex h-28 items-end gap-1.5">
              {bars.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className={`flex-1 rounded-t-sm ${i === bars.length - 1 ? "bg-blue-500/100" : "bg-blue-500/100/30"}`}
                />
              ))}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-white/10 pt-5">
              {[
                { k: "Load time", v: "0.8s" },
                { k: "Last backup", v: "2h ago" },
                { k: "Threats blocked", v: "147" },
              ].map((s) => (
                <div key={s.k}>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{s.k}</p>
                  <p className="mt-1 font-display text-lg font-bold text-white">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FaqPreview() {
  return (
    <section className="border-t border-white/10 bg-[#070B1E] py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Fair questions, straight answers.
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Everything about working with us — timelines, updates and how we deliver.
          </p>
          <Link
            to="/faq"
            data-testid="faq-view-all-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300"
          >
            View all FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="text-left text-sm font-semibold text-white hover:text-blue-400">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

function BookingSection() {
  return (
    <section id="book" className="relative overflow-hidden bg-[#070B1E] py-20 lg:py-28">
      <div className="bg-grid-dark absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute top-[-20%] right-[-10%] h-[480px] w-[480px] rounded-full bg-blue-600/25 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-[-30%] left-[-5%] h-[420px] w-[420px] rounded-full bg-violet-600/20 blur-[130px]" />
      <Starfield />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <Reveal>
          <img
            src="/gstudio-logo.png"
            alt="G-Studio — We Build. You Grow."
            className="mx-auto h-20 w-auto rounded-2xl mix-blend-screen"
            data-testid="book-logo"
          />
          <div className="mt-4 flex justify-center"><Eyebrow dark>Get Started</Eyebrow></div>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Book a call. We'll handle the rest.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-400">
            Tell us about your business and what your website needs to do. Call or text us directly, or send a
            message — we reach back within one business day.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+15078411801"
              data-testid="book-call-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/30 transition-colors hover:bg-blue-500/100"
            >
              <Phone className="h-4 w-4" />
              Call (507) 841-1801
            </a>
            <a
              href="mailto:bengall.work3@gmail.com"
              data-testid="book-email-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-colors hover:border-blue-400 hover:text-blue-300"
            >
              <Mail className="h-4 w-4" />
              Email Us
            </a>
          </div>
          <ul className="mx-auto mt-9 flex max-w-2xl flex-wrap justify-center gap-x-8 gap-y-3">
            {["Free intro call — no pressure", "Reply within one business day", "Hosting and security handled for you"].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-sm text-slate-300">
                <Check className="h-4 w-4 text-emerald-400" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Services />
      <Process />
      <Upkeep />
      <FaqPreview />
      <BookingSection />
    </main>
  );
}
